import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSelector } from 'react-redux';
import { axiosInstance } from '../../../Axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const PageShell = styled(Box)(() => ({
  minHeight: 'calc(100vh - 120px)',
  padding: 24,
  borderRadius: 8,
  background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%)',
}));

const HeroPanel = styled(Paper)(() => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 8,
  padding: 24,
  color: '#fff',
  background: 'linear-gradient(135deg, #111827, #334155)',
  boxShadow: '0 22px 48px rgba(15, 23, 42, .18)',
  '&::after': {
    content: '""', position: 'absolute', right: -40, bottom: -70, width: 270, height: 155,
    background: 'repeating-linear-gradient(-45deg, #f59e0b 0 16px, #111827 16px 32px)',
    transform: 'rotate(-6deg)', opacity: .9,
  },
}));

const REPORTS = {
  portfolio: {
    title: 'Project Portfolio Report',
    description: 'Project locations, dates, duration, and current delivery status.',
    icon: BusinessIcon,
  },
  schedule: {
    title: 'Project Schedule Report',
    description: 'Planned work packages and their scheduled date ranges.',
    icon: CalendarMonthIcon,
  },
  workforce: {
    title: 'Workforce Allocation Report',
    description: 'Employees allocated to each project, roles, dates, and status.',
    icon: EngineeringIcon,
  },
};

const getToken = (loginInfo) => loginInfo?.token || loginInfo?.result?.userToken;
const unwrap = (response) => response?.data?.data || response?.data?.result || response?.data || [];
const asArray = (value) => Array.isArray(value) ? value : [];
const valueOf = (row, keys, fallback = '-') => {
  const key = keys.find((item) => row?.[item] !== undefined && row?.[item] !== null && row?.[item] !== '');
  return key ? row[key] : fallback;
};
const formatDate = (value) => {
  if (!value || value === '-') return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-GB');
};
const projectIdOf = (row) => valueOf(row, ['projectId', 'ProjectId', 'id'], '');
const projectNameOf = (row) => valueOf(row, ['projectName', 'ProjectName', 'name'], 'Unnamed project');

const reportColumns = {
  portfolio: [
    ['projectName', 'Project'], ['location', 'Location'], ['startDate', 'Start date', 'date'],
    ['endDate', 'End date', 'date'], ['status', 'Status'],
  ],
  schedule: [
    ['projectName', 'Project'], ['planTitle', 'Plan / work package'], ['fromDate', 'From', 'date'],
    ['toDate', 'To', 'date'], ['status', 'Status'],
  ],
  workforce: [
    ['projectName', 'Project'], ['employeeName', 'Employee'], ['designation', 'Role'],
    ['fromDate', 'From', 'date'], ['toDate', 'To', 'date'], ['status', 'Status'],
  ],
};

const normalizeRows = (type, rows, projects) => rows.map((row, index) => {
  const project = projects.find((item) => String(projectIdOf(item)) === String(projectIdOf(row)));
  if (type === 'portfolio') {
    return {
      id: projectIdOf(row) || index,
      projectName: projectNameOf(row),
      location: valueOf(row, ['location', 'projectLocation']),
      startDate: valueOf(row, ['startDate', 'fromDate']),
      endDate: valueOf(row, ['endDate', 'toDate']),
      status: valueOf(row, ['status', 'statusName'], 'Pending'),
    };
  }
  if (type === 'schedule') {
    return {
      id: valueOf(row, ['projectPlanId', 'id'], index),
      projectId: projectIdOf(row), projectName: valueOf(row, ['projectName'], project ? projectNameOf(project) : '-'),
      planTitle: valueOf(row, ['planTitle', 'title', 'dayTitle']),
      fromDate: valueOf(row, ['fromDate', 'startDate', 'planDate']),
      toDate: valueOf(row, ['toDate', 'endDate', 'planDate']),
      status: valueOf(row, ['statusName', 'status'], 'Planning'),
    };
  }
  return {
    id: valueOf(row, ['projectEmployeeId', 'id'], index),
    projectId: projectIdOf(row), projectName: valueOf(row, ['projectName'], project ? projectNameOf(project) : '-'),
    employeeName: valueOf(row, ['employeeName', 'fullName', 'name']),
    designation: valueOf(row, ['designationName', 'designation', 'roleName', 'role']),
    fromDate: valueOf(row, ['fromDate', 'startDate', 'allocationDate']),
    toDate: valueOf(row, ['toDate', 'endDate']),
    status: valueOf(row, ['statusName', 'status'], 'Allocated'),
  };
});

const fileNamePart = (value, fallback) => String(value || fallback)
  .replace(/[^a-z0-9]+/gi, '-')
  .replace(/^-|-$/g, '')
  .toLowerCase() || fallback;

const dateKeyOf = (value) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export default function ConstructionReports() {
  const { loginInfo } = useSelector((state) => state.user);
  const [reportType, setReportType] = useState('portfolio');
  const [projectId, setProjectId] = useState('all');
  const [projects, setProjects] = useState([]);
  const [data, setData] = useState({ portfolio: [], schedule: [], workforce: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    const token = getToken(loginInfo);
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const load = async () => {
      setLoading(true); setError('');
      try {
        const projectResponse = await axiosInstance.get('/Project/GetProjectList', {
          ...config, params: { Offset: 0, RecordCount: 1000, ProjectName: '' },
        });
        const projectRows = asArray(unwrap(projectResponse));
        const [scheduleResult, workforceResult] = await Promise.allSettled([
          axiosInstance.get('/ProjectPlan/GetProjectPlanList', {
            ...config, params: { ProjectPlanId: 0, ProjectId: 0, PlanTitle: '', Offset: 0, RecordCount: 5000 },
          }),
          axiosInstance.get('/ProjectEmployee/GetProjectEmployeeList', {
            ...config, params: { ProjectId: 0, EmployeeName: '' },
          }),
        ]);
        if (!active) return;
        const schedules = scheduleResult.status === 'fulfilled' ? asArray(unwrap(scheduleResult.value)) : [];
        const workforce = workforceResult.status === 'fulfilled' ? asArray(unwrap(workforceResult.value)) : [];
        setProjects(projectRows);
        setData({
          portfolio: normalizeRows('portfolio', projectRows, projectRows),
          schedule: normalizeRows('schedule', schedules, projectRows),
          workforce: normalizeRows('workforce', workforce, projectRows),
        });
        if (scheduleResult.status === 'rejected' || workforceResult.status === 'rejected') {
          setError('Some report data could not be loaded. Project information is still available.');
        }
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load reports.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [loginInfo, refreshKey]);

  const rows = useMemo(() => data[reportType].filter((row) => (
    projectId === 'all' || String(projectIdOf(row)) === String(projectId)
  )), [data, reportType, projectId]);
  const columns = reportColumns[reportType];
  const report = REPORTS[reportType];

  const createPdf = () => {
    try {
      setError('');
      const selectedProject = projects.find((item) => String(projectIdOf(item)) === String(projectId));
      const projectLabel = selectedProject ? projectNameOf(selectedProject) : 'All projects';
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      const generatedAt = new Date();
      const documentRef = `CM-${reportType.toUpperCase()}-${dateKeyOf(generatedAt).replace(/-/g, '')}`;

      doc.setFillColor(15, 23, 42); doc.rect(0, 0, pageWidth, 38, 'F');
      doc.setFillColor(245, 158, 11); doc.rect(0, 35, pageWidth, 3, 'F');
      doc.setTextColor(245, 158, 11); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
      doc.text('CONSTRUCTION MANAGEMENT', margin, 12);
      doc.setTextColor(255, 255, 255); doc.setFontSize(20); doc.text(report.title.toUpperCase(), margin, 23);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(203, 213, 225);
      doc.text(`Project: ${projectLabel}`, pageWidth - margin, 13, { align: 'right' });
      doc.text(`Document: ${documentRef}`, pageWidth - margin, 13, { align: 'right' });
      doc.text(`Generated: ${generatedAt.toLocaleString('en-GB')}`, pageWidth - margin, 21, { align: 'right' });

      doc.setTextColor(15, 23, 42); doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
      doc.text(String(rows.length), margin, 51);
      doc.setTextColor(100, 116, 139); doc.setFontSize(8); doc.text('TOTAL RECORDS', margin, 57);
      doc.setTextColor(15, 23, 42); doc.setFontSize(18);
      doc.text(String(selectedProject ? 1 : projects.length), margin + 42, 51);
      doc.setTextColor(100, 116, 139); doc.setFontSize(8); doc.text('PROJECTS COVERED', margin + 42, 57);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(71, 85, 105);
      doc.text(`Scope: ${projectLabel}  |  ${report.description}`, margin + 92, 53);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42); doc.text('REPORT DETAILS', margin, 66);

      autoTable(doc, {
        startY: 70,
        head: [columns.map(([, label]) => label)],
        body: rows.length
          ? rows.map((row) => columns.map(([key, , type]) => String(type === 'date' ? formatDate(row[key]) : row[key] ?? '-')))
          : [[`No records available for ${projectLabel}.`, ...columns.slice(1).map(() => '')]],
        theme: 'grid',
        margin: { top: 18, left: margin, right: margin, bottom: 15 },
        styles: { font: 'helvetica', fontSize: 8, cellPadding: 2.7, textColor: [51, 65, 85], lineColor: [226, 232, 240], lineWidth: .15, overflow: 'linebreak' },
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });

      const pages = doc.getNumberOfPages();
      for (let page = 1; page <= pages; page += 1) {
        doc.setPage(page);
        if (page > 1) {
          doc.setFillColor(15, 23, 42); doc.rect(0, 0, pageWidth, 12, 'F');
          doc.setTextColor(245, 158, 11); doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.text('CONSTRUCTION MANAGEMENT', margin, 7.5);
          doc.setTextColor(255, 255, 255); doc.text(report.title.toUpperCase(), pageWidth - margin, 7.5, { align: 'right' });
        }
        doc.setDrawColor(226, 232, 240); doc.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
        doc.text(`${documentRef}  ·  Confidential`, margin, pageHeight - 5);
        doc.text(`Page ${page} of ${pages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
      }

      const reportName = fileNamePart(report.title.replace(/ report$/i, ''), 'report');
      const projectName = fileNamePart(selectedProject ? projectNameOf(selectedProject) : 'all-projects', 'all-projects');
      doc.save(`${projectName}-${reportName}-${dateKeyOf(new Date())}.pdf`);
    } catch (pdfError) {
      setError(pdfError?.message || 'Unable to download the PDF report.');
    }
  };

  return (
    <PageShell>
      <HeroPanel sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
          <Box><Typography variant="overline" sx={{ color: '#fbbf24', fontWeight: 900 }}>Site intelligence</Typography>
            <Typography variant="h4" fontWeight={900} sx={{ color: '#fbbf24' }}>Construction Reports</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,.72)', mt: 1 }}>Review project, schedule, and workforce information and produce print-ready PDF reports.</Typography>
          </Box>
          <Button variant="contained" startIcon={<PictureAsPdfIcon />} disabled={loading} onClick={createPdf} sx={{ color: '#111827', fontWeight: 900, background: 'linear-gradient(135deg,#fbbf24,#f97316)' }}>Download PDF</Button>
        </Stack>
      </HeroPanel>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {Object.entries(REPORTS).map(([key, item]) => { const Icon = item.icon; const selected = reportType === key; return (
          <Card key={key} onClick={() => setReportType(key)} sx={{ flex: 1, cursor: 'pointer', border: selected ? '2px solid #f59e0b' : '1px solid #e2e8f0', boxShadow: selected ? '0 12px 28px rgba(245,158,11,.18)' : 'none' }}>
            <CardContent><Stack direction="row" spacing={2}><Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: selected ? '#f59e0b' : '#e2e8f0' }}><Icon /></Box>
              <Box><Typography fontWeight={900}>{item.title}</Typography><Typography variant="body2" color="text.secondary">{item.description}</Typography></Box></Stack></CardContent>
          </Card>
        ); })}
      </Stack>

      <Paper sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
          <Stack direction="row" spacing={1.5} alignItems="center"><AssessmentIcon sx={{ color: '#d97706' }} /><Box><Typography variant="h6" fontWeight={900}>{report.title}</Typography><Typography variant="body2" color="text.secondary">Live report preview</Typography></Box></Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 230 }}><InputLabel>Project</InputLabel><Select label="Project" value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              <MenuItem value="all">All projects</MenuItem>{projects.map((project) => <MenuItem key={projectIdOf(project)} value={projectIdOf(project)}>{projectNameOf(project)}</MenuItem>)}
            </Select></FormControl>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setRefreshKey((key) => key + 1)}>Refresh</Button>
            <Chip label={`${rows.length} records`} sx={{ fontWeight: 800, bgcolor: '#ffedd5', color: '#9a3412' }} />
          </Stack>
        </Stack>
        {error && <Alert severity="warning" sx={{ borderRadius: 0 }}>{error}</Alert>}
        {loading ? <Stack alignItems="center" sx={{ py: 9 }} spacing={2}><CircularProgress sx={{ color: '#f59e0b' }} /><Typography color="text.secondary">Preparing reports…</Typography></Stack> : (
          <TableContainer sx={{ maxHeight: 520 }}><Table stickyHeader size="small"><TableHead><TableRow>{columns.map(([key, label]) => <TableCell key={key} sx={{ bgcolor: '#1e293b', color: '#fff', fontWeight: 900 }}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>{rows.length ? rows.map((row) => <TableRow hover key={`${reportType}-${row.id}`}>{columns.map(([key, , type]) => <TableCell key={key}>{type === 'date' ? formatDate(row[key]) : row[key]}</TableCell>)}</TableRow>) : <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 7, color: 'text.secondary' }}>No records are available for this selection.</TableCell></TableRow>}</TableBody>
          </Table></TableContainer>
        )}
      </Paper>
    </PageShell>
  );
}
